import { ref, onBeforeMount } from 'vue'
import NykForm from "../CustomForm/NykForm/NykForm.vue"

export default {
  name: "SendDocumentBlock",
  components: {
    NykForm
  },
  props: {
    formId: {
      type: String,
      required: true
    },
    formGuid: {
      type: String,
      required: true
    },
    allowFileUpload: {
      type: Boolean,
      required: false,
      default: false
    },
    apiPath: {
      type: String,
      required: true
    },
    advisorsSearchApiPath: {
      type: String,
      required: true
    },
    antiForgeryToken: {
      type: String,
      required: true
    },
    allowInternationalNumber: {
      type: Boolean,
      required: false
    },
    allowedFileTypes: {
      type: String,
      required: false
    },
    maxFileSizeInBytes: {
      type: Number,
      required: false,
      default: 10485760 // 10 MB
    },
    receiverEmailValidDomains: {
      type: Array,
      required: false
    },
    labels: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const formElements = ref([]);
    const queryParams = ref({});
    const queryParamFields = ref(['senderEmail', 'advisorId', 'receiverEmail']);

    onBeforeMount(() => {
      const queryString = window.location.search
      const urlParams = new URLSearchParams(queryString)
      const centerId = urlParams.get("centerId") || urlParams.get("centerid")

      queryParams.value = {
        ...centerId ? { centerId } : {}
      };
    })

    formElements.value = [
      {
        key: "personType",
        contentType: "CustomChoiceElementBlock",
        displayName: "personType",
        properties: {
          items: [
            {
              caption: props.labels.privatePersonTypeLabel,
              value: "private-person",
              checked: false
            },
            {
              caption: props.labels.businessPersonTypeLabel,
              value: "business",
              checked: false
            },
            {
              caption: props.labels.otherPersonTypeLabel,
              value: "other",
              checked: false
            }
          ],
          label: props.labels.personTypeLabel
        }
      },
      {
        key: 'cvrNumber',
        contentType: "CustomCvrNumberElementBlock",
        displayName: "cvrNumber",
        properties: {
          conditions: [
            {
              field: "personType",
              operator: "Equals",
              fieldValue: "business"
            }
          ],
          label: props.labels.cvrNumberLabel,
          maxLength: 500,
          clearButtonAriaLabel: props.labels.clearButtonAriaLabel,
          company: "company",
          phone: "phone",
          email: "senderEmail"
        },
      },
      {
        key: 'company',
        contentType: "CustomTextboxElementBlock",
        displayName: "company",
        properties: {
          conditions: [
            {
              field: "personType",
              operator: "Equals",
              fieldValue: "business"
            },
          ],
          label: props.labels.companyLabel,
          maxLength: 500,
          clearButtonAriaLabel: props.labels.clearButtonAriaLabel
        },
      },
      {
        key: 'fullName',
        contentType: "CustomTextboxElementBlock",
        displayName: "fullName",
        properties: {
          conditions: [
            {
              field: "personType",
              operator: "Equals",
              fieldValue: "private-person"
            },
            {
              field: "personType",
              operator: "Equals",
              fieldValue: "other"
            }
          ],
          conditionCombination: "Any",
          label: props.labels.nameLabel,
          maxLength: 500,
          clearButtonAriaLabel: props.labels.clearButtonAriaLabel
        },
      },
      {
        key: 'cprNumber',
        contentType: "CustomCprNumberElementBlock",
        displayName: "cprBumber",
        properties: {
          conditions: [
            {
              field: "personType",
              operator: "Equals",
              fieldValue: "private-person"
            }
          ],
          label: props.labels.cprNumberLabel,
          clearButtonAriaLabel: props.labels.clearButtonAriaLabel
        },
      },
      {
        key: 'senderEmail',
        contentType: "CustomEmailElementBlock",
        displayName: "senderEmail",
        properties: {
          validators: [
            {
              type: "RequiredValidator",
              model: {
                message: props.labels.requiredValidationFailureMessage
              }
            },
            {
              type: "EmailFormatValidator",
              model: {
                message: props.labels.emailFormationValidationFailureMessage
              }
            }
          ],
          label: props.labels.emailLabel,
          maxLength: 500,
          clearButtonAriaLabel: props.labels.clearButtonAriaLabel
        },
      },
      {
        key: 'phone',
        contentType: "CustomPhoneElementBlock",
        displayName: "phone",
        properties: {
          label: props.labels.phoneLabel,
          countryCodeLabel: props.labels.countryCodeLabel,
          allowInternationalNumber: props.allowInternationalNumber,
          maxLength: 20,
          clearButtonAriaLabel: props.labels.clearButtonAriaLabel
        },
      },
      {
        key: "contactType",
        contentType: "NykCheckbox",
        displayName: "contactType",
        properties: {
          label: props.labels.contactTypeLabel,
        },
      },
      {
        key: 'advisorId',
        contentType: "NykAdvisorSelector",
        displayName: "advisorId",
        properties: {
          validators: [
            {
              type: "RequiredValidator",
              model: {
                message: props.labels.requiredValidationFailureMessage
              }
            }
          ],
          conditions: [
            {
              field: "contactType",
              operator: "Equals",
              fieldValue: false
            },
            {
              field: "contactType",
              operator: "Equals",
              fieldValue: undefined
            }
          ],
          conditionCombination: "Any",
          label: props.labels.advisorLabel,
          countryCodeFormatValidationFailureMessage: props.labels.countryCodeFormatValidationFailureMessage,
          apiPath: props.advisorsSearchApiPath,
          maxLength: 500,
          clearButtonAriaLabel: props.labels.clearButtonAriaLabel
        },
      },
      {
        key: 'receiverEmail',
        contentType: "CustomEmailElementBlock",
        displayName: "receiverEmail",
        properties: {
          validators: [
            {
              type: "RequiredValidator",
              model: {
                message: props.labels.requiredValidationFailureMessage
              }
            },
            {
              type: "EmailFormatValidator",
              model: {
                message: props.labels.emailFormationValidationFailureMessage
              }
            },
            {
              type: "EmailDomainValidator",
              model: {
                message: props.labels.receiverEmailFormatValidationFailureMessage,
                validDomains: props.receiverEmailValidDomains
              }
            }
          ],
          conditions: [
            {
              field: "contactType",
              operator: "Equals",
              fieldValue: true
            }
          ],
          label: props.labels.receiverEmailLabel,
          maxLength: 500,
          clearButtonAriaLabel: props.labels.clearButtonAriaLabel
        },
      },
      {
        key: 'comment',
        contentType: "CustomTextareaElementBlock",
        displayName: "comment",
        properties: {
          label: props.labels.commentLabel,
          maxLength: 1000,
          clearButtonAriaLabel: props.labels.clearButtonAriaLabel
        },
      },
      {
        key: "files",
        contentType: "CustomFileUploadElementBlock",
        displayName: "files",
        properties: {
          validators: [
            {
              type: "MaxFileSizeValidator",
              model: {
                message: props.labels.fileSizeValidationFailureMessage,
                sizeInBytes: props.maxFileSizeInBytes
              }
            },
            {
              type: "AllowedExtensionsValidator",
              model: {
                accept: props.allowedFileTypes
              }
            },
          ],
          label: props.labels.fileUploadLabel,
          description: props.labels.fileUploadDescription,
          allowMultiple: true
        }
      },
      {
        key: "submit-button",
        contentType: "CustomSubmitButtonElementBlock",
        displayName: "submit-button",
        properties: {
          label: props.labels.submitButtonLabel,
        }
      }
    ];

    return {
      formElements,
      queryParams,
      queryParamFields
    }
  }
}
